/* Home-page behaviors — ported 1:1 from <VaultHero> and <ExclusiveVaultDeal>,
   plus the product-card image gallery (theme-only). The gallery lives here
   rather than in pdp.js because cards render on collection, search, index,
   page.vault AND the PDP, and this is the only card-aware file that
   layout/theme.liquid loads unconditionally (:92).
   No-ops when the sections aren't on the page. */
(function () {
  "use strict";
  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ── Product-card image gallery — arrows drive the index, CSS drives the
     motion (markup: snippets/product-card.liquid) ─────────────────────────
     Placed first in this file deliberately: cards render on every template
     while the hero and the shelf render on one each, so this is the block that
     must not sit downstream of code that can throw. (A nested IIFE would NOT
     buy that — one placed after a throwing statement never executes. Ordering
     or try/catch are the only things that do.)

     JS owns the index and nothing else. It writes --i inline on the track and
     the stylesheet turns that into a transitioned translateX. Desktop hover is
     a pure CSS peek (--i:1 on .td-pcard:hover), so the slide the owner asked
     for still works with this file absent — the arrows are the only part that
     needs JS, which is why they ship display:none and are switched on by
     .is-gallery at the bottom of this sweep.

     A scoped-root sweep, not delegation: each card carries its own index, and
     nothing in this theme injects cards after render (the shelf filter only
     sets .hidden on the wrapper). No collision with the document listeners in
     cart.js (:132, :159) or theme.js (:143) either — they all match
     e.target.closest("[data-…]"), an ancestor-or-self test, and these arrows
     are SIBLINGS of the quick-add, never its ancestors. No stopPropagation
     anywhere, and .td-pcard__media is a plain <div>, so an arrow click cannot
     reach an anchor. No `reduce` check: the theme's rule (see the hero, below)
     is that JS suppresses only self-starting motion, and there is no autoplay
     here. */
  var cards = document.querySelectorAll("[data-pcard]");

  /* CLICK SUPPRESSION FOR A COMPLETED DRAG — one pair for the whole page, on
     `document`. NOT card-scoped: a commit is 48-72px and --grid-gap is 20px, so
     a finger routinely lifts over the NEIGHBOUR, and a card-scoped guard would
     open the wrong product. Capture + stopPropagation, not preventDefault
     alone, because quick-add is a <button> on a delegated listener; all three
     delegated click listeners here (cart.js:132, :159, theme.js:143) are
     `document` BUBBLE, so one capture listener beats them at any defer order.
     Armed at pointerup and only for a gesture that LOCKED — a tap never arms
     it. Disarmed by the click it eats, by the next pointerdown (a moved touch
     usually produces NO click, and a stale flag would eat a later arrow tap
     stone dead), and by a 700ms expiry that exists only to bound the one click
     with no pointerdown in front of it: a keyboard Enter. */
  var pcardDragUntil = 0;
  if (cards.length) {
    document.addEventListener("click", function (e) {
      if (!pcardDragUntil || Date.now() > pcardDragUntil) return;
      pcardDragUntil = 0;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    document.addEventListener("pointerdown", function () { pcardDragUntil = 0; }, true);
  }

  cards.forEach(function (card) {
    var track = card.querySelector("[data-pcard-track]");
    var prev = card.querySelector("[data-pcard-prev]");
    var next = card.querySelector("[data-pcard-next]");
    if (!track || !prev || !next) return;
    var n = track.children.length;
    if (n < 2) return;
    var live = card.querySelector("[data-pcard-live]");
    // GESTURE SURFACE = the media box, not the track. Both arrows (z 3, 36/44px,
    // centred on the very midline a horizontal swipe uses), the badge and the
    // sold-out scrim are SIBLINGS of the thumb <a>, so a track-scoped
    // pointerdown never sees them — 88px of a 328px phone box dead, and a moved
    // touch on a <button> emits no click either, so it fails silently while the
    // arrow presses down under the finger. One level up needs no exclusion
    // list, and it is why the sold-out scrim needs NO pointer-events edit.
    var media = card.querySelector(".td-pcard__media") || track;
    var cur = 0, paged = false, drag = null;

    // Read what is ON SCREEN, not what we last wrote. While the CSS hover peek
    // is up the track is showing shot 2 with no inline --i at all, so stepping
    // from a remembered 0 would set --i:1 — the shot already visible — and the
    // first press of "next" would look dead on every card, every time, on
    // desktop. Reading the computed value is the ground truth, so it stays
    // correct even if the peek rule is later rewritten; transcribing the CSS
    // selector into JS instead (matches(":hover") && a state class) duplicates
    // an invariant across two files that will drift. Unregistered custom
    // property: "" when unset -> NaN -> 0. Nothing else here reads the DOM.
    function shown() {
      var v = parseInt(window.getComputedStyle(track).getPropertyValue("--i"), 10);
      return v > 0 ? v : 0;
    }
    // Where the PIXELS are, in slide-widths — a different question, asked only
    // by the drag. --i is unregistered, so it is not interpolated: go() flips it
    // to the destination on frame 1 while the transform glides there for
    // 240-420ms. A drag starting inside that window (2nd of two swipes, swipe
    // after an arrow tap, swipe during the hybrid hover peek) would otherwise
    // follow from the DESTINATION and jump a full slide in its first frame.
    // lw is the LAYOUT width — a % translateX resolves against the border box,
    // which is the unit computed transform reports px in. Deliberately not the
    // rect width the finger delta uses, which carries .td-thumb's scale(1.03).
    function visible(lw) {
      var t = window.getComputedStyle(track).transform;
      if (t.indexOf("matrix") !== 0) return shown();
      var p = t.slice(t.indexOf("(") + 1).split(",");
      var v = -parseFloat(t.indexOf("matrix3d") === 0 ? p[12] : p[4]) / lw;
      return isFinite(v) ? v : shown();
    }
    function go(i) {
      cur = ((i % n) + n) % n;
      paged = true;
      track.style.setProperty("--i", cur);
      // The slides are alt="", so without this an arrow press is completely
      // silent — a control with no perceivable effect. Only the card the
      // visitor activated ever mutates, and it starts empty, so nothing
      // announces on load.
      if (live) live.textContent = "Image " + (cur + 1) + " of " + n;
    }
    // The lock guard is invariant 2 again: mid-drag --i is fractional and
    // parseInt("0.42") is 0. Guarding step() covers arrow clicks AND arrow
    // keydowns from one place. `drag.lock`, not `drag` — an unlocked drag has
    // written nothing and must never disable a card's arrows for the session.
    function step(d) { if (drag && drag.lock) return; go(shown() + d); }
    // Hand the index back to CSS once the pointer/focus has left AND we are
    // home again, so the hover peek re-arms. A card the visitor paged away
    // from stays where they put it — silently rewinding a deliberate click is
    // the worse surprise. The live region is NOT cleared: NVDA voices
    // live-region removals in some configurations, and the next announcement
    // differs in the common case anyway. Harmless on touch, where there is no
    // peek to re-arm. The lock test keeps a synthetic mouseleave — which a
    // finger generates on a hybrid — from removing --i out from under a live
    // gesture; settle() re-runs this after, since mouseleave fires only once.
    function release() {
      if ((drag && drag.lock) || !paged || cur !== 0) return;
      paged = false;
      track.style.removeProperty("--i");
    }
    function onKey(e) {
      if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
    }

    prev.addEventListener("click", function () { step(-1); });
    next.addEventListener("click", function () { step(1); });
    prev.addEventListener("keydown", onKey);
    next.addEventListener("keydown", onKey);
    card.addEventListener("mouseleave", release);
    // focusout as well as mouseleave: a keyboard user who pages back round to
    // slide 1 and tabs away would otherwise leave an inline --i:0 parked on
    // the track, and the next visitor's first hover would not peek. Same
    // relatedTarget guard the hero uses, so moving focus prev->next inside the
    // card does not fire it.
    card.addEventListener("focusout", function (e) {
      if (!card.contains(e.relatedTarget)) release();
    });

    /* ── SWIPE — the finger moves the SAME --i the arrows move ──────────────
       translateX(calc(var(--i,0) * -100%)) already reads 0.42 as "42% of the
       way to shot 2", so live-follow is this block's existing variable written
       at pointer rate: one model, one writer at a time, still no state class.
       TOUCH AND PEN ONLY — a mouse drag would write an inline --i on a 10px
       twitch and kill that card's CSS peek for the visit (invariant 1), and
       desktop already has hover and arrows. Same boundary as the hero.
       INVARIANT 2 UNCHANGED: the commit base is shown(), read from the DOM,
       never remembered — and read at the LOCK, so a plain TAP does zero layout
       and zero style reads. NO rAF; after the lock the handler only WRITES,
       so recalc defers a frame and multiple writes per frame collapse. The
       transform stays a PERCENTAGE (invariant 4) and a shelf-hidden card at
       width 0 cannot receive a pointerdown at all. */

    // Restore the track exactly as the LOCK found it. "" is the honest snapshot
    // when there was no inline --i: removing it hands the index back to the
    // stylesheet and the peek re-arms, which is what release() does after an
    // arrow press. An abort routed through go() instead would stamp a permanent
    // inline --i:1 on a hybrid card that was only peeking, and release()'s
    // `cur !== 0` test would then refuse to remove it for the session.
    // transition-duration is restored in the same synchronous block as the
    // position write — transitions start from the AFTER-change style, so the
    // snap is a real glide.
    function settle(d, i) {
      track.style.removeProperty("transition-duration");
      track.style.removeProperty("will-change");
      if (i === null || i === d.base) {
        if (d.inline === "") track.style.removeProperty("--i");
        else track.style.setProperty("--i", d.inline);
      } else {
        go(i);
      }
      if (!card.matches(":hover")) release();   // see release(): mouseleave fires once
    }

    media.addEventListener("pointerdown", function (e) {
      // `drag.lock`, not `drag`: a second finger must not re-enter a LIVE
      // gesture, but an unlocked drag has written nothing, so replacing it is
      // safe — and it self-heals the one strand capture cannot cover, a pen
      // leaving the box inside the first 8px. isPrimary drops a pinch's second
      // finger. Quick-add is the ONE exclusion: 10px of thumb wobble on a 42px
      // pill is a normal tap, and a locked drag would have that click eaten
      // above — a silent failure to add to cart. Arrows are NOT excluded: under
      // 8px their click fires untouched, and over 8px a drag begun on "next"
      // and travelling left commits +1, the operation that arrow performs.
      if (drag && drag.lock) return;
      if (!e.isPrimary || e.pointerType === "mouse") return;
      if (e.target.closest("[data-quick-add]")) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, lock: false, t0: e.timeStamp };
    });

    // No {passive:true}: pointermove is not a scroll-blocking event type,
    // touch-action governs the compositor, and nothing here preventDefaults.
    media.addEventListener("pointermove", function (e) {
      var d = drag;
      if (!d || e.pointerId !== d.id) return;
      var dx = e.clientX - d.x, dy = e.clientY - d.y;

      if (!d.lock) {
        // UNDECIDED IS NOT REJECTED — never latch an abort here. A terminal
        // verdict on one 8px sample runs a ratio test inside the sensor's noise
        // floor: a 7px finger-roll at touchdown (dx 8, dy 7 -> 8 <= 8.4) would
        // kill a clean 120px swipe with no second chance. Deferring is free —
        // nothing is written, the page still scrolls under pan-y, and if the
        // browser commits to that scroll it fires pointercancel, the
        // authoritative abort. A real vertical scroll can never satisfy this:
        // deltas are from pointerdown, so 200px of dy dominates any dx drift.
        // 1.2 bias = the hero's — ambiguous means vertical, because scrolling
        // past a grid is common and paging one card is rare.
        if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
        // Rect for the FINGER divisor (it carries .td-thumb's hover scale(1.03)
        // and .td-pcard's :active scale(.995), so the shot tracks 1:1);
        // offsetWidth for visible(). Both guard w === 0 -> Infinity ->
        // "--i:NaN", which invalidates the calc() and drops the transform.
        var w = track.getBoundingClientRect().width, lw = track.offsetWidth;
        if (!w || !lw) { drag = null; return; }
        d.w = w;
        d.base = shown();                            // commit origin, integer
        d.vis = visible(lw);                         // render origin, fractional
        d.inline = track.style.getPropertyValue("--i");
        d.lx = e.clientX;                            // follow measured from HERE,
        d.px = e.clientX;                            // so the 8px slop is not
        d.pt = e.timeStamp;                          // double-counted
        d.v = 0;
        d.lock = true;
        // Order is load-bearing: visible() is read BEFORE the duration is
        // zeroed and --i is written AFTER, all in one task, so zeroing cancels
        // the running transition and no frame is painted at the old target.
        track.style.transitionDuration = "0s";
        track.style.willChange = "transform";        // only between lock and settle
        // Capture at the LOCK, never at pointerdown. Touch gets implicit
        // capture free; pen does not, so a pen leaving the box would stop
        // delivering moves and never deliver up, parking a fractional --i with
        // its transition suppressed. Capturing at pointerdown would claim every
        // TAP, and a captured pointer retargets the compatibility click to the
        // capture element — breaking tap -> PDP, the primary conversion path.
        // try/catch (the only one in this theme): setPointerCapture throws
        // NotFoundError if the finger lifted between the browser queueing this
        // move and us handling it, and it is last so a throw cannot skip the
        // style setup above.
        try { media.setPointerCapture(e.pointerId); } catch (err) { /* already released */ }
      }

      // >=30ms (~2 frame) window: sampling every move yields ~0 at pointerup,
      // because the up lands on the last move's coordinates.
      var dt = e.timeStamp - d.pt;
      if (dt >= 30) { d.v = (e.clientX - d.px) / dt; d.px = e.clientX; d.pt = e.timeStamp; }

      // Higher --i = further left = later shot, so a finger moving left raises
      // it. Past either end the surplus is divided by 3: the track still
      // follows, visibly resists, and answers "how many shots are there" inside
      // the gesture with no bounce to unwind. NOT a wrap — the arrows wrap, and
      // may, because they are discrete and nothing is mid-flight.
      var i = d.vis - (e.clientX - d.lx) / d.w;
      if (i < 0) i /= 3;
      else if (i > n - 1) i = n - 1 + (i - (n - 1)) / 3;
      track.style.setProperty("--i", i);
    });

    media.addEventListener("pointerup", function (e) {
      var d = drag;                             // id check BEFORE nulling
      if (!d || e.pointerId !== d.id) return;
      drag = null;
      if (!d.lock) return;                      // a tap: nothing was written
      var dx = e.clientX - d.x, span = e.timeStamp - d.t0;
      var v = (e.timeStamp - d.pt) > 120 ? 0 : d.v;   // a stale sample is not a flick
      // Fallback for a flick so fast every pointermove coalesced inside the
      // 30ms window — the sampler returns exactly 0 and a real flick would drop
      // to the position rule. Bounded so a slow crawl cannot borrow it.
      if (!v && span > 0 && span < 250) v = dx / span;
      var dir = 0;
      if (Math.abs(v) > 0.5) dir = v < 0 ? 1 : -1;
      // 18% lands on the hero's hard-coded 48px at the smallest real box
      // (267px, 4-up desktop); the 72px cap stops the 572px featured card
      // needing a 103px haul for the same result.
      else if (Math.abs(dx) > Math.min(d.w * 0.18, 72)) dir = dx < 0 ? 1 : -1;
      // ARM ON COMMIT, NOT ON LOCK — pointerup is still the only path to a
      // click, but arming for ANY locked drag eats the click of a "walking
      // tap": a press on an arrow that drifts 10-15px before lifting clears the
      // 8px lock, aborts below the commit threshold, and then has its click
      // swallowed, so the arrow silently does nothing. Only a gesture that
      // actually moved the image to a new slide should suppress the click
      // underneath it. (Same fix applied to the PDP hero in pdp.js.)
      if (dir) pcardDragUntil = Date.now() + 700;
      var i = d.base + dir;
      settle(d, i < 0 ? 0 : (i > n - 1 ? n - 1 : i));    // clamp, never wrap
    });

    // pointercancel = scroll takeover, pinch, long-press menu, OS steal.
    // lostpointercapture = the failure that is invisible AND PERMANENT: a
    // locked drag whose pointer vanishes with neither terminal event (tab
    // backgrounded mid-gesture, OS drag session, pen off the digitiser). Left
    // uncovered the track keeps a fractional --i, a suppressed transition and a
    // pinned will-change layer, and the lock guard kills that card's swipe for
    // the session. It always fires when capture ends and always AFTER the
    // terminal events, so on normal paths drag is null and it costs nothing.
    function abort(e) {
      var d = drag;
      if (!d || e.pointerId !== d.id) return;
      drag = null;
      if (d.lock) settle(d, null);
    }
    media.addEventListener("pointercancel", abort);
    media.addEventListener("lostpointercapture", abort);

    // Last: arrows become visible, and the media box reserves touch-action,
    // only once the wiring succeeded.
    card.classList.add("is-gallery");
  });

  /* ── Hero carousel ──────────────────────────────────────────────────── */
  document.querySelectorAll("[data-hero]").forEach(function (hero) {
    var slides = hero.querySelectorAll("[data-hero-slide]");
    var media = hero.querySelectorAll("[data-hero-media]");
    var dots = hero.querySelectorAll("[data-hero-dot]");
    var track = hero.querySelector("[data-hero-track]");
    var n = slides.length;
    if (n < 2) return;
    var cur = 0, timer = 0, paused = false;
    var delay = parseInt(hero.getAttribute("data-autoplay"), 10) || 5500;

    function setActive(list, i) {
      list.forEach(function (el, j) { el.classList.toggle("is-active", j === i); });
    }
    function go(i) {
      cur = (i + n) % n;
      setActive(slides, cur); setActive(media, cur);
      dots.forEach(function (d, j) {
        d.classList.toggle("is-active", j === cur);
        d.setAttribute("aria-selected", j === cur ? "true" : "false");
      });
      slides.forEach(function (s, j) {
        if (j === cur) { s.removeAttribute("aria-hidden"); s.removeAttribute("inert"); }
        else { s.setAttribute("aria-hidden", "true"); s.setAttribute("inert", ""); }
      });
      restart();
    }
    function tick() { if (!paused && !reduce) go(cur + 1); }
    function restart() { window.clearInterval(timer); if (!reduce) timer = window.setInterval(tick, delay); }

    dots.forEach(function (d, j) { d.addEventListener("click", function () { go(j); }); });
    var prev = hero.querySelector("[data-hero-prev]"), next = hero.querySelector("[data-hero-next]");
    if (prev) prev.addEventListener("click", function () { go(cur - 1); });
    if (next) next.addEventListener("click", function () { go(cur + 1); });

    hero.addEventListener("mouseenter", function () { paused = true; });
    hero.addEventListener("mouseleave", function () { paused = false; });
    hero.addEventListener("focusin", function () { paused = true; });
    hero.addEventListener("focusout", function (e) { if (!hero.contains(e.relatedTarget)) paused = false; });

    // Touch swipe (touch-action: pan-y keeps vertical scroll native)
    var start = null, swiped = false;
    if (track) {
      track.addEventListener("pointerdown", function (e) {
        if (e.pointerType === "mouse") return;
        start = { x: e.clientX, y: e.clientY, id: e.pointerId };
      });
      track.addEventListener("pointerup", function (e) {
        var t = start; start = null;
        if (!t || e.pointerId !== t.id) return;
        var dx = e.clientX - t.x, dy = e.clientY - t.y;
        if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          swiped = true;
          go(dx < 0 ? cur + 1 : cur - 1);
        }
      });
      track.addEventListener("pointercancel", function () { start = null; });
      track.addEventListener("click", function (e) {
        if (swiped) { swiped = false; e.preventDefault(); e.stopPropagation(); }
      }, true);
    }

    restart();
  });

  /* ── Vault shelf — client-side family filter (matches <VaultSection>) ── */
  document.querySelectorAll("[data-shelf]").forEach(function (shelf) {
    var chips = Array.prototype.slice.call(shelf.querySelectorAll("[data-shelf-chips] [data-family]"));
    var grid = shelf.querySelector("[data-shelf-grid]");
    var countEl = shelf.querySelector("[data-shelf-count]");
    if (!grid || !chips.length) return;
    var items = Array.prototype.slice.call(grid.children);

    function apply(fam, label) {
      var shown = 0;
      items.forEach(function (it) {
        var match = fam === "all" || it.getAttribute("data-family") === fam;
        it.hidden = !match;
        it.removeAttribute("data-slot");
        it.style.gridColumn = "";
        it.style.gridRow = "";
        if (match) { shown++; if (it.hasAttribute("data-reveal")) it.classList.add("is-in"); }
      });
      // The 2x2 hero tile is opt-in: only grids marked data-shelf-featured get
      // it, so a plain uniform grid (the collection page) stays uniform.
      if (grid.hasAttribute("data-shelf-featured")) {
        var first = items.filter(function (it) { return !it.hidden; })[0];
        if (first) { first.setAttribute("data-slot", "featured"); first.style.gridColumn = "span 2"; first.style.gridRow = "span 2"; }
      }
      if (countEl) countEl.textContent = label + ": " + shown + (shown === 1 ? " product" : " products");
    }

    chips.forEach(function (chip) {
      chip.addEventListener("click", function () {
        var fam = chip.getAttribute("data-family");
        chips.forEach(function (c) {
          var on = c === chip;
          c.classList.toggle("td-chip--selected", on);
          c.setAttribute("aria-pressed", on ? "true" : "false");
        });
        apply(fam, chip.querySelector("span").textContent);
        var url = new URL(window.location);
        if (fam === "all") url.searchParams.delete("v"); else url.searchParams.set("v", fam.toLowerCase());
        history.replaceState(null, "", url);
      });
    });

    var v = new URL(window.location).searchParams.get("v");
    if (v) {
      var target = chips.filter(function (c) { return c.getAttribute("data-family").toLowerCase() === v; })[0];
      if (target) target.click();
    }
  });

  /* The exclusive deal's pointer parallax and scroll-progress fallback were
     removed with the floating pack — the product now lives in the plate
     artwork, so there is nothing left to animate. */
})();
